import { ProductMasterBatchJobOperationTypeEnum } from '@ulysses-inc/harami_api_client'
import { useCallback, useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { isHTTPError } from 'src/state/middleware/saga/handleHttpError'
import { useMutationExecuteMasterBatchJob } from '../api/useMutationExecuteMasterBatchJob'
import { useMutationGenerateMasterBatchJobPresignedUrl } from '../api/useMutationGenerateMasterBatchJobPresignedUrl'

type UseMasterFileUploadProps = {
  masterUuid: string
  onClose: () => void
  operationType: ProductMasterBatchJobOperationTypeEnum
}

export const useMasterFileUpload = ({
  masterUuid,
  onClose,
  operationType,
}: UseMasterFileUploadProps) => {
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar()
  const generatePresignedUrlMutation =
    useMutationGenerateMasterBatchJobPresignedUrl()
  const executeBatchJobMutation = useMutationExecuteMasterBatchJob()
  const [isUploading, setIsUploading] = useState(false)

  const isProcessing =
    generatePresignedUrlMutation.isPending ||
    executeBatchJobMutation.isPending ||
    isUploading

  const handleUploadError = useCallback(
    (error: unknown) => {
      if (isHTTPError(error) && error.status === 409) {
        showErrorSnackbar(
          '一括処理中はCSVファイルをアップロードできません。完了後にもう一度お試しください',
        )
      } else {
        showErrorSnackbar(
          'ファイルのアップロードに失敗しました。再度アップロードを行ってください',
        )
      }
    },
    [showErrorSnackbar],
  )

  const handleUploadSuccess = useCallback(
    (fileName: string) => {
      showSuccessSnackbar(`${fileName}の処理を開始しました`)
      onClose()
    },
    [showSuccessSnackbar, onClose],
  )

  const uploadFileToS3 = useCallback(
    async (
      url: string,
      fields: Record<string, string>,
      file: File,
    ): Promise<void> => {
      const formData = new FormData()
      // 署名済みフィールドを先に追加（file は最後に追加する必要がある）
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value)
      })
      formData.append('file', file)

      const uploadResponse = await fetch(url, {
        method: 'POST',
        body: formData,
      })
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status: ${uploadResponse.status}`)
      }
    },
    [],
  )

  const executeBatchJob = useCallback(
    (jobUuid: string, fileName: string) => {
      executeBatchJobMutation.mutate(
        { masterUuid, jobUuid },
        {
          onSuccess: () => handleUploadSuccess(fileName),
          onError: error => handleUploadError(error),
        },
      )
    },
    [
      executeBatchJobMutation,
      masterUuid,
      handleUploadSuccess,
      handleUploadError,
    ],
  )

  const uploadFile = useCallback(
    (selectedFile: File) => {
      generatePresignedUrlMutation.mutate(
        {
          masterUuid,
          request: { fileName: selectedFile.name, operationType },
        },
        {
          onSuccess: async response => {
            setIsUploading(true)
            try {
              await uploadFileToS3(response.url, response.fields, selectedFile)
              executeBatchJob(response.jobUuid, selectedFile.name)
            } catch (error) {
              handleUploadError(error)
            } finally {
              setIsUploading(false)
            }
          },
          onError: handleUploadError,
        },
      )
    },
    [
      generatePresignedUrlMutation,
      masterUuid,
      operationType,
      uploadFileToS3,
      executeBatchJob,
      handleUploadError,
    ],
  )

  return { uploadFile, isProcessing }
}
